/**
 * consent.ts — pure consent state machine for habitat sensing.
 *
 * States:
 *   "unset"     → banner may be shown when a mode wants the camera
 *   "granted"   → sensing auto-resumes
 *   "dismissed" → user said "Not now": never re-ask on mode switches
 *   "stopped"   → user pressed Stop: stay stopped until they explicitly
 *                 resume (privacy dialog) — mode switches never restart it
 */

export const CONSENT_KEY = "habitat:consent";

export type ConsentState = "unset" | "granted" | "dismissed" | "stopped";
export type ConsentAction = "allow" | "dismiss" | "stop" | "forget";

const STATES: readonly ConsentState[] = ["unset", "granted", "dismissed", "stopped"];

export function isConsentState(value: string | null): value is ConsentState {
  return value != null && (STATES as readonly string[]).includes(value);
}

/** Coerce anything read from localStorage into a valid state (absent/corrupt → "unset"). */
export function readConsentState(raw: string | null): ConsentState {
  return isConsentState(raw) ? raw : "unset";
}

/** Pure transition table: given an action, what's the next state? (The
    current state doesn't affect the outcome for any action here, but it's
    accepted for symmetry with typical reducer shapes and future actions
    that might need it.) */
export function nextConsentState(_current: ConsentState, action: ConsentAction): ConsentState {
  switch (action) {
    case "allow": return "granted";
    case "dismiss": return "dismissed";
    case "stop": return "stopped";
    case "forget": return "unset";
  }
}

/** Show the consent banner only when nothing has been decided yet. */
export function shouldShowBanner(state: ConsentState): boolean {
  return state === "unset";
}

/** Auto-start sensing quietly (no banner) only once consent was granted. */
export function shouldAutoStart(state: ConsentState): boolean {
  return state === "granted";
}
